import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { CombinedMappingDialog, useMappingReviewDialog } from '@verantos-dev/feature-code-mapping';
import { Button } from '@verantos-dev/shared-components';

/**
 * MappingResultsAction Component
 *
 * Displays an action button for OMOP mapping results in the Research Assistant chat.
 * When clicked, opens a dialog showing the full mapping results fetched from S3.
 *
 * Similar to ConceptGroupReviewAction, this component:
 * - Renders an action button in the chat message
 * - Opens a dialog when clicked
 * - Fetches and displays mapping results from S3
 * - Handles loading and error states
 */

interface MappingResultsActionProps {
  omopMetadata: {
    session_id: string;
    file_name: string;
    original_file_name?: string;
    total_codes?: number;
    s3_uri?: string;
    status?: string;
    mapping_summary?: unknown;
  };
}

export const MappingResultsAction = ({ omopMetadata }: MappingResultsActionProps) => {
  const intl = useIntl();

  // Use the existing mapping review dialog hook
  const dialogProps = useMappingReviewDialog();
  const { handleOpenDialog } = dialogProps;

  // Handle button click - fetch and open mapping results
  const handleClick = useCallback(async () => {
    try {
      // Open dialog with session_id and file_name - dialog will fetch results from S3
      await handleOpenDialog(omopMetadata.session_id, omopMetadata.file_name);
    } catch (error) {
      console.error('Failed to open mapping results:', error);
    }
  }, [handleOpenDialog, omopMetadata]);

  return (
    <>
      <Button
        id="review-mapping-results"
        displayLabel={intl.formatMessage({
          id: 'projects.researchAssistant.mappingResults.reviewLink',
        })}
        variant="outlined"
        color="primary"
        size="small"
        onClickHandler={handleClick}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          fontWeight: 500,
          paddingInline: 3,
        }}
      />
      {dialogProps.isDialogOpen && <CombinedMappingDialog {...dialogProps} />}
    </>
  );
};
